import { FormModal, type SubmitProps } from '@/components/form-modal';
import { Button } from '@/components/ui/button';
import { DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/hooks/use-toast';
import { deleteAnnouncement, getAnnouncements } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import type { Announcement } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { z } from 'zod';

const deleteAnnouncementSchema = z.object({});

export interface DeleteAnnouncementProps {
  announcement: Announcement;
  trigger?: ReactNode;
}

export function DeleteAnnouncement({ announcement, trigger }: DeleteAnnouncementProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({ mutationFn: deleteAnnouncement });

  const onSubmit = async ({ closeModal }: SubmitProps<z.infer<typeof deleteAnnouncementSchema>>) => {
    try {
      await mutateAsync(announcement.id);
      await queryClient.invalidateQueries(getAnnouncements());
      toast({ description: 'Announcement deleted successfully' });
      closeModal();
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        description: airServerErrorFrom(error)?.userFacingMessage ?? 'Failed to delete announcement.',
      });
    }
  };

  return (
    <FormModal
      modalTitle="Delete announcement"
      trigger={
        trigger ?? (
          <Button variant="outline" size="sm" title="Delete">
            <Trash2 className="h-4 w-4" />
          </Button>
        )
      }
      formSchema={deleteAnnouncementSchema}
      onSubmit={onSubmit}
      confirmBtnLabel="Delete"
      submitButtonProps={{ variant: 'destructive' }}
      dialogContentProps={{ className: 'max-w-md' }}
    >
      {() => (
        <DialogDescription>
          Are you sure you want to delete {announcement.title ? `“${announcement.title}”` : 'this announcement'}? This
          action cannot be undone.
        </DialogDescription>
      )}
    </FormModal>
  );
}
